import { Component, Inject, OnInit } from '@angular/core';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { NgxDropzoneModule, NgxDropzoneChangeEvent } from 'ngx-dropzone';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmationService } from 'primeng/api';
import { ToastrService } from 'ngx-toastr';
import { BASE_PATH } from '@elesgergo/ikont-web-client-sdk';
import { downloadBlob } from '@shared';

@Component({
  selector: 'app-formly-document-upload',
  standalone: true,
  imports: [CommonModule, NgxDropzoneModule, ButtonModule, TooltipModule],
  templateUrl: './formly-document-upload.component.html',
  styleUrls: ['./formly-document-upload.component.scss'],
  // Deliberately not OnPush: this component mutates plain component
  // properties (deleting/files) and the shared formly model (via
  // getDocument()/setDocument()) from inside async callbacks (HTTP
  // subscribe, FileReader) rather than through @Input() reference changes -
  // under OnPush those updates never trigger a re-render, and none of that
  // imperative access pattern benefits from OnPush's optimization anyway.
})
export class FormlyDocumentUploadComponent
  extends FieldType<FieldTypeConfig>
  implements OnInit
{
  files: File[] = [];
  deleting = false;

  // True while the user is swapping out an already-persisted document for a
  // new one (triggered by "Csere", not the plain upload-when-empty flow).
  // The old document is only ever deleted after the new one is confirmed
  // uploaded+attached on save - see onSelect()/replacesDocumentUuid.
  replacing = false;
  private documentBeforeReplace: any = null;

  constructor(
    private readonly http: HttpClient,
    private readonly toastr: ToastrService,
    private readonly confirmationService: ConfirmationService,
    @Inject(BASE_PATH) private readonly basePath: string
  ) {
    super();
  }

  ngOnInit() {
    // init
  }

  getDocument() {
    if (!this.to.documentPath) {
      // Ha nincs documentPath, akkor a közvetlen mezőt használjuk
      return this.formControl.value;
    }

    const pathParts = this.to.documentPath.split('.');
    let document = this.field.parent?.model;

    for (let i = 0; i < pathParts.length; i++) {
      const part = pathParts[i];
      if (!document) return null;

      // Ellenőrizzük, hogy tömb hivatkozás-e
      if (part.includes('[') && part.includes(']')) {
        // Például: "qualifications[index]" alakú
        const arrayName = part.substring(0, part.indexOf('['));
        const indexExpr = part.substring(part.indexOf('[') + 1, part.indexOf(']'));

        // Ha az index 'index' vagy hasonló, akkor a field.parent.key-t használjuk
        let index = indexExpr;
        if (indexExpr === 'index' || indexExpr === '{index}') {
          index = this.field.parent?.key;
        }

        // Elérjük a tömb elemet
        document = document[arrayName][index];
      } else {
        // Normál tulajdonság elérés
        document = document[part];
      }
    }

    return document;
  }

  setDocument(documentData: any) {
    if (!this.to.documentPath) {
      // Ha nincs documentPath, közvetlenül a formControl értékét állítjuk
      this.formControl.setValue(documentData);
      return;
    }

    const pathParts = this.to.documentPath.split('.');
    let current = this.field.parent?.model;

    for (let i = 0; i < pathParts.length - 1; i++) {
      const part = pathParts[i];

      if (part.includes('[') && part.includes(']')) {
        const arrayName = part.substring(0, part.indexOf('['));
        const indexExpr = part.substring(part.indexOf('[') + 1, part.indexOf(']'));

        let index = indexExpr;
        if (indexExpr === 'index' || indexExpr === '{index}') {
          index = this.field.parent?.key;
        }

        if (!current[arrayName]) {
          current[arrayName] = [];
        }
        if (!current[arrayName][index]) {
          current[arrayName][index] = {};
        }

        current = current[arrayName][index];
      } else {
        if (!current[part]) {
          current[part] = {};
        }
        current = current[part];
      }
    }

    const lastPart = pathParts[pathParts.length - 1];
    current[lastPart] = documentData;
  }

  isDisabled() {
    if (typeof this.to.disabled === 'boolean') {
      return this.to.disabled;
    }

    if (typeof this.to.disabled === 'string') {
      return this.to.disabled === 'true';
    }

    if (this.to.parentFormDisabled) {
      return true;
    }

    return false;
  }

  isDownloadDisabled() {
    return !this.getDocument()?.fileUrl;
  }

  // The backend returns an object with every field set to null (not
  // null/undefined itself) when the worker has no document for this slot -
  // a plain `getDocument()` truthy check would still treat that as "a
  // document exists" and render the existing-document panel with NaN/
  // undefined values instead of the upload dropzone.
  hasDocument(): boolean {
    const document = this.getDocument();
    return !!(document?.fileName || document?.fileUrl);
  }

  onSelect(event: NgxDropzoneChangeEvent) {
    this.files = event.addedFiles;

    if (this.files.length === 0) {
      return;
    }

    // If this pick is replacing an already-persisted document, carry its
    // UUID along so the backend can delete it - but only *after* the new
    // file is successfully uploaded and attached on save, never before.
    const replacesDocumentUuid = this.replacing
      ? this.documentBeforeReplace?.fileUrl || undefined
      : undefined;

    const file = this.files[0];
    const reader = new FileReader();
    reader.onload = () => {
      this.setDocument({
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        fileUrl: null,
        uploadDate: new Date(),
        // Base64 data URL - stripped down to raw bytes server-side on save.
        fileContent: reader.result as string,
        replacesDocumentUuid,
      });
    };
    reader.onerror = () => {
      this.toastr.error('A fájl beolvasása sikertelen.');
      this.files = [];
    };
    reader.readAsDataURL(file);
  }

  // Reveals the dropzone over an already-persisted document without
  // deleting it - the old document stays untouched (and downloadable) in
  // the model until a new file is actually picked and saved.
  startReplace(): void {
    this.documentBeforeReplace = this.getDocument();
    this.replacing = true;
  }

  // Backs out of a replace before a new file was picked - the model was
  // never touched by startReplace(), so there's nothing to restore.
  cancelReplace(): void {
    this.replacing = false;
    this.documentBeforeReplace = null;
  }

  // Three cases share this handler: discarding a freshly selected file that
  // was never saved (nothing to tell the backend), backing out of a
  // in-progress replace after a new file was already picked (restore the
  // original persisted document rather than leaving the slot empty), and
  // deleting an already-persisted document outright (getDocument().fileUrl
  // set, not replacing) - the last one is a real, irreversible ESBR delete,
  // so it's confirmed first.
  onRemove() {
    if (this.replacing) {
      this.files = [];
      this.replacing = false;
      this.setDocument(this.documentBeforeReplace);
      this.documentBeforeReplace = null;
      return;
    }

    const document = this.getDocument();

    if (!document?.fileUrl) {
      this.files = [];
      this.setDocument(null);
      return;
    }

    this.confirmationService.confirm({
      header: 'Dokumentum törlése',
      message: `Biztosan törli a(z) "${document.fileName}" dokumentumot? Ez a művelet nem vonható vissza.`,
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.deletePersistedDocument(document.fileUrl, document.fileName),
    });
  }

  private deletePersistedDocument(documentUuid: string, fileName: string) {
    if (this.deleting) {
      return;
    }

    this.deleting = true;
    this.http
      .delete(
        `${this.basePath}/v0/worker-management/document/${encodeURIComponent(documentUuid)}`
      )
      .subscribe({
        next: () => {
          this.deleting = false;
          this.files = [];
          this.setDocument(null);
          this.toastr.success('Dokumentum törölve.');
        },
        error: () => {
          this.deleting = false;
          this.toastr.error(`A(z) "${fileName}" dokumentum törlése sikertelen.`);
        },
      });
  }

  // Downloads the already-persisted document (getDocument().fileUrl is the
  // ESBR document UUID once the worker has been saved - see
  // WorkerManagementController.downloadWorkerDocument on the backend). No
  // busy/spinner state here on purpose - once the blob arrives, saving it is
  // the browser's own native download UI to handle, not something this
  // button needs to reflect.
  downloadDocument() {
    const document = this.getDocument();
    if (!document?.fileUrl) {
      return;
    }

    this.http
      .get(
        `${this.basePath}/v0/worker-management/download-document/${encodeURIComponent(document.fileUrl)}`,
        { responseType: 'blob' }
      )
      .subscribe({
        next: (blob) => downloadBlob(blob, document.fileName || 'dokumentum'),
        error: () => this.toastr.error('A dokumentum letöltése sikertelen.'),
      });
  }

  formatFileSize(bytes: unknown): string {
    const numBytes = Number(bytes);
    if (!Number.isFinite(numBytes) || numBytes <= 0) {
      return '0 Bytes';
    }
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.min(
      Math.floor(Math.log(numBytes) / Math.log(k)),
      sizes.length - 1
    );
    return parseFloat((numBytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}
